import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { logError } from '@/api/backend';
import { agentApi } from '@/api/backend/agent';
import { useNestingFailureMessage } from '@/api/backend/nestingErrors';
import { usePartsErrorStore } from '@/api/errors';
import {
  useActiveGroup,
  useMaterialGroups,
  useMaterialGroupState,
  useSaveAllGroupChanges,
} from '@/api/materialsGroup';
import {
  computeCutlistNestingHash,
  isNestingPollSettled,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { usePricingStore } from '@/api/pricing';
import {
  isValidPart,
  partsSelector,
  useCutlistParts,
  useCutlistState,
} from '@/api/store';
import Card from '@/blocks/Card';
import { StarterTierNesting } from '@/interfaces/StarterTierNesting';
import { usePollAgentNestingJobs } from '@/queries/agent';
import { getCutlistQuery } from '@/queries/crud';
import { agentKeys, cutlistsKeys } from '@/queries/keys';

import { InfoMessage } from './InfoMessage';

export const AgentNestingBlock = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const cutlistId = id as string;

  const groups = useMaterialGroups();
  const parts = useCutlistParts();
  const activeGroup = useActiveGroup();
  const isBeamGroup = activeGroup?.type === 'beams';

  const queryClient = useQueryClient();
  const [saveAllMaterialGroups] = useSaveAllGroupChanges();
  const setMixedResults = useMixedNestingStore((state) => state.setResults);
  const nestingState = useMixedNestingStore((state) => state.nestingState);
  const snapshotHash = useMixedNestingStore((state) => state.snapshotHash);
  const { setNestingState, setPolling, setSnapshotHash, setFailedGroups } =
    useMixedNestingStore.getState();

  const nestedGroups = useMixedNestingStore((state) =>
    state.groups.filter((group) => group.id === activeGroup?.id)
  );

  const { data: pollData, isError: pollError } =
    usePollAgentNestingJobs(cutlistId);

  const failureMessage = useNestingFailureMessage(activeGroup?.id);

  const liveHash = computeCutlistNestingHash(groups, parts);
  const dirty = snapshotHash !== null && liveHash !== snapshotHash;

  const nestableGroups = groups.filter(
    (group) =>
      group.type !== 'beams' &&
      parts.some((part) => part.groupId === group.id && isValidPart(part))
  );
  const hasSomethingToCompute =
    nestableGroups.length > 0 ||
    parts.some((part) => part.partType === 'beam' && part.beamMaterialId);

  const runNesting = async () => {
    setNestingState('computing');
    usePartsErrorStore.getState().resetPartsNestingError();
    try {
      await saveAllMaterialGroups();
      setSnapshotHash(
        computeCutlistNestingHash(
          useMaterialGroupState.getState().groups,
          partsSelector(useCutlistState.getState())
        )
      );
      await agentApi.triggerNestingJobs(cutlistId);
      queryClient.removeQueries({ queryKey: agentKeys.nestingJobs(cutlistId) });
      setPolling(true);
    } catch {
      setFailedGroups(null);
      setPolling(false);
      setNestingState('failed');
    }
  };

  React.useEffect(() => {
    if (pollError) {
      setFailedGroups(null);
      setPolling(false);
      setNestingState('failed');
      return;
    }

    if (!pollData) return;

    if (pollData.done) {
      const { cutlist } = pollData;
      queryClient.setQueryData(cutlistsKeys.getOne(cutlistId), cutlist);
      setMixedResults(cutlist);

      const pricing = usePricingStore.getState();
      pricing.setError(null);
      pricing.setAmountVat(cutlist.totalAmountInclVAT);
      pricing.setAmountNoVat(cutlist.totalAmountExclVAT);
      pricing.setPricing(cutlist.pricingLineItems);
      pricing.toggleManualQuote(cutlist.manualQuote);

      usePartsErrorStore.getState().resetPartsNestingError();
      setSnapshotHash(
        computeCutlistNestingHash(
          useMaterialGroupState.getState().groups,
          partsSelector(useCutlistState.getState())
        )
      );
      setPolling(false);
      setNestingState('done');
      return;
    }

    const failedGroups = pollData.groups.filter(
      (group) => group.status === 'failed'
    );
    if (!failedGroups.length || !isNestingPollSettled(pollData)) return;

    setFailedGroups(failedGroups);
    setPolling(false);
    setNestingState('failed');
    queryClient
      .fetchQuery(getCutlistQuery(cutlistId))
      .then(setMixedResults)
      .catch(logError);
  }, [
    pollData,
    pollError,
    t,
    setMixedResults,
    setNestingState,
    setPolling,
    setSnapshotHash,
    setFailedGroups,
    queryClient,
    cutlistId,
  ]);

  const isComputing = nestingState === 'computing';
  const canNavigate = nestingState === 'done' && !dirty;

  return (
    <aside id="nesting-parts">
      {!isBeamGroup && (
        <StarterTierNesting
          groups={nestedGroups}
          onCalculate={canNavigate ? undefined : runNesting}
          canCalculate={hasSomethingToCompute}
          calculateLabel={t('cutlist-summary.nesting.computeNestingCta')}
          smallCalculate
          complete={canNavigate}
          isComputing={isComputing}
          dirty={dirty}
          failureMessage={failureMessage}
        />
      )}
      {isBeamGroup && failureMessage && (
        <Card>
          <InfoMessage message={failureMessage} variant="error" />
        </Card>
      )}
    </aside>
  );
};
